/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */

import useToast from '@/Composables/app/useToast';
import { usePage } from '@inertiajs/vue3';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';

window.axios = axios;

const page = usePage();
const { toast } = useToast();

window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
window.axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig<any>) => {
    const authUrls = ['/logout', '/user/profile-information', '/user/password'];
    if (
      page.props.auth &&
      page.props.organization &&
      !authUrls.includes(config.url ?? '') &&
      config.baseURL !== ''
    ) {
      config.baseURL = `/${page.props.organization.identifier}`;
    }

    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  },
);

window.axios.interceptors.response.use(
  function (response: AxiosResponse) {
    return response;
  },
  function (error: any) {
    if (typeof error != undefined) {
      if (error.response) {
        if (error.response.status === 500)
          toast.error({
            title: 'Request failed',
            message:
              'Something went wrong during the request. Please try again.',
          });
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */

import Echo from 'laravel-echo';

import Pusher from 'pusher-js';
window.Pusher = Pusher;

const domain = window.location.origin;

/**
 * Laravel Cloud's managed Reverb cluster injects VITE_REVERB_* at build time.
 * When those are present Echo connects to Reverb (the 'reverb' broadcaster
 * uses the Pusher protocol with an empty cluster); otherwise the local
 * Soketi configuration from VITE_PUSHER_* is used unchanged.
 */
const connectionOptions =
  import.meta.env.VITE_REVERB_APP_KEY && import.meta.env.VITE_REVERB_HOST
    ? {
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: import.meta.env.VITE_REVERB_PORT ?? 443,
        wssPort: import.meta.env.VITE_REVERB_PORT ?? 443,
        forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
      }
    : {
        broadcaster: 'pusher',
        key: import.meta.env.VITE_PUSHER_APP_KEY,
        cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER ?? 'mt1',
        wsHost: import.meta.env.VITE_PUSHER_HOST
          ? import.meta.env.VITE_PUSHER_HOST
          : `ws-${import.meta.env.VITE_PUSHER_APP_CLUSTER}.pusher.com`,
        wsPort: import.meta.env.VITE_PUSHER_PORT ?? 80,
        wssPort: import.meta.env.VITE_PUSHER_PORT ?? 443,
        forceTLS: (import.meta.env.VITE_PUSHER_SCHEME ?? 'https') === 'https',
      };

window.Echo = new Echo({
  ...connectionOptions,
  enabledTransports: ['ws', 'wss'],
  authorizer: (channel: any, options: any) => {
    return {
      authorize: (socketId: string, callback: any) => {
        axios
          .post(domain + '/broadcasting/auth', {
            socket_id: socketId,
            channel_name: channel.name,
          })
          .then((response) => {
            callback(false, response.data);
          })
          .catch((error) => {
            callback(true, error);
          });
      },
    };
  },
});
